import OpenAI from 'openai';
import { AVAILABLE_MODELS, anthropic, ModelName } from '../utils/modelConfig';

if (!process.env.REACT_APP_OPENAI_API_KEY) {
  console.error('OpenAI API key is missing');
}

if (!process.env.REACT_APP_ANTHROPIC_API_KEY) {
  console.error('Anthropic API key is missing');
}

const openaiInstance = new OpenAI({
  apiKey: process.env.REACT_APP_OPENAI_API_KEY || '',
  dangerouslyAllowBrowser: true
});

export async function generateIdeas(topic: string, modelName: ModelName = 'gpt-4o-mini'): Promise<string[]> {
  try {
    if (!topic.trim()) {
      throw new Error('Topic is required');
    }

    const modelConfig = AVAILABLE_MODELS[modelName];
    if (!modelConfig) {
      throw new Error(`Invalid model name: ${modelName}`);
    }

    const systemPrompt = "You are a creative assistant that generates innovative ideas. For each idea, provide a concise but detailed description.";
    const userPrompt = `Generate 3-5 unique and creative ideas about: ${topic}. Make each idea specific and actionable.

Format each idea on a new line starting with a dash (-). Example:
- First innovative idea with specific details
- Second unique idea with clear implementation
- Third creative idea with practical approach`;

    let content: string | null | undefined;

    if (modelConfig.provider === 'openai') {
      try {
        const completion = await openaiInstance.chat.completions.create({
          model: modelConfig.name,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: userPrompt }
          ],
          temperature: modelConfig.temperature,
          max_tokens: modelConfig.maxTokens
        });

        content = completion.choices[0]?.message?.content;
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes('API key')) {
            throw new Error('OpenAI API key is invalid or not configured');
          } else if (error.message.includes('model')) {
            throw new Error('Invalid model configuration. Please check your model settings.');
          }
        }
        throw error;
      }

    } else if (modelConfig.provider === 'anthropic') {
      try {
        const response = await anthropic.messages.create({
          model: modelConfig.name,
          max_tokens: modelConfig.maxTokens,
          temperature: modelConfig.temperature,
          system: systemPrompt,
          messages: [
            { role: "user", content: userPrompt }
          ]
        });

        content = response.content[0].text;
      } catch (error) {
        if (error instanceof Error) {
          if (error.message.includes('API key')) {
            throw new Error('Anthropic API key is invalid or not configured');
          } else if (error.message.includes('model')) {
            throw new Error('Invalid model configuration. Please check your model settings.');
          }
        }
        throw error;
      }
    } else {
      throw new Error(`Unsupported model provider: ${modelConfig.provider}`);
    }

    if (!content) {
      throw new Error('No content received from API');
    }

    const ideas = content
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.startsWith('-'))
      .map(line => line.substring(1).trim());

    if (ideas.length === 0) {
      throw new Error('No valid ideas were generated');
    }

    return ideas;

  } catch (error) {
    console.error('Error details:', error);
    if (error instanceof Error) {
      throw new Error(`Failed to generate ideas: ${error.message}`);
    }
    throw new Error('Failed to generate ideas. Please try again.');
  }
}

export async function expandIdea(idea: string, modelName: ModelName = 'gpt-4o-mini'): Promise<string> {
  try {
    const modelConfig = AVAILABLE_MODELS[modelName];

    if (modelConfig.provider === 'openai') {
      const completion = await openaiInstance.chat.completions.create({
        model: modelConfig.name,
        messages: [
          {
            role: "system",
            content: "You are a helpful assistant that expands ideas into more detailed concepts."
          },
          {
            role: "user",
            content: `Expand this idea into 3-5 more specific sub-ideas: ${idea}

Please format each sub-idea on a new line, starting with a dash (-).
Example:
- First sub-idea with more detail
- Second sub-idea with specific aspects
- Third sub-idea exploring another angle`
          }
        ],
        max_tokens: modelConfig.maxTokens,
        temperature: modelConfig.temperature
      });

      const content = completion.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No content received from API');
      }
      return content;

    } else if (modelConfig.provider === 'anthropic') {
      const message = await anthropic.messages.create({
        model: modelConfig.name,
        max_tokens: modelConfig.maxTokens,
        temperature: modelConfig.temperature,
        system: "You are a helpful assistant that expands ideas into more detailed concepts.",
        messages: [
          {
            role: "user",
            content: `Expand this idea into 3-5 more specific sub-ideas: ${idea}

Please format each sub-idea on a new line, starting with a dash (-).
Example:
- First sub-idea with more detail
- Second sub-idea with specific aspects
- Third sub-idea exploring another angle`
          }
        ]
      });

      const content = message.content[0].text;
      if (!content) {
        throw new Error('No content received from API');
      }
      return content;
    }

    throw new Error(`Unsupported model provider: ${modelConfig.provider}`);
  } catch (error) {
    console.error('AI API error:', error);
    throw error;
  }
}

export async function rewriteIdea(content: string, prompt: string, modelName: ModelName = 'gpt-4o-mini', regenerate: boolean = false): Promise<string> {
  try {
    const modelConfig = AVAILABLE_MODELS[modelName];
    if (!modelConfig) {
      throw new Error(`Invalid model name: ${modelName}`);
    }

    const systemPrompt = "You are a helpful assistant that rewrites and improves ideas based on specific prompts.";
    const userPrompt = `Please rewrite this idea: ${content}\n\nInstructions: ${prompt}`;

    if (modelConfig.provider === 'openai') {
      try {
        const completion = await openaiInstance.chat.completions.create({
          model: modelConfig.name,
          messages: [
            { role: "system", content: systemPrompt },
            { role: "user", content: userPrompt }
          ],
          temperature: modelConfig.temperature,
          max_tokens: modelConfig.maxTokens
        });

        const newContent = completion.choices[0]?.message?.content;
        if (!newContent) {
          throw new Error('No content received from OpenAI');
        }
        return newContent;
      } catch (error: any) {
        if (error.message.includes('API key')) {
          throw new Error('OpenAI API key is invalid or not configured');
        }
        throw error;
      }
    } else if (modelConfig.provider === 'anthropic') {
      try {
        const message = await anthropic.messages.create({
          model: modelConfig.name,
          max_tokens: modelConfig.maxTokens,
          temperature: modelConfig.temperature,
          system: systemPrompt,
          messages: [
            { role: "user", content: userPrompt }
          ]
        });

        const newContent = message.content[0].text;
        if (!newContent) {
          throw new Error('No content received from Anthropic');
        }
        return newContent;
      } catch (error: any) {
        if (error.message.includes('API key')) {
          throw new Error('Anthropic API key is invalid or not configured');
        }
        throw error;
      }
    }

    throw new Error(`Unsupported model provider: ${modelConfig.provider}`);
  } catch (error) {
    console.error('Error in rewriteIdea:', error);
    throw error;
  }
}
